import { Theme } from "@mui/material";
import { CvssScoreType } from "~/lib/score";
import { getImpactLabelUpdated } from "~/components/impact/Updated/impactUpdatedUtils";

export const CVSS_VERSIONS = ["2.0", "3.0", "3.1", "4.0"] as const;
export type CvssVersion = (typeof CVSS_VERSIONS)[number];

export const isCvssVersion = (value: any): value is CvssVersion => {
  return CVSS_VERSIONS.includes(value);
};

export const NONE_VECTOR = "0.0/CVSS:3.0";

export const CVSS_VERSION_RX = /^(?:.*\/)?CVSS:([\d.]+)(?:\/.*)*$/;

export type CvssFieldValue = {
  title: string;
  acronym: string;
  value?: number;
  scalar: number;
  color: string;
};
export type CvssFieldValues = { [valueId: string]: CvssFieldValue };
export type CvssField = {
  title: string;
  description: string;
  acronym: string;
  values: CvssFieldValues;
};
export type CvssFields = { [fieldId: string]: CvssField | null };

export type CvssMetric = { field: CvssField; value: CvssFieldValue };

export const cvssMetric =
  (fields: CvssFields) =>
  (str: string): CvssMetric | null => {
    const [id, v] = str.split(":");
    const field = fields[id];

    if (field == null) {
      console.error(`CVSS: Cannot find field "${id}""`);
      return null;
    }

    const value = field.values[v];
    if (value == null) {
      console.error(
        `CVSS: Cannot find value "${v}" of field "${field.acronym}"`,
      );
      return null;
    }

    return { field, value };
  };

// CVSS v2.0 https://www.first.org/cvss/v2/guide
// CVSS v3.0 https://www.first.org/cvss/v3-0/specification-document
// CVSS v3.1 https://www.first.org/cvss/v3-1/specification-document
// CVSS v4.0 https://www.first.org/cvss/v4-0/specification-document
export const cvss2Fields = (theme: Theme): CvssFields => {
  return {
    av: {
      title: "Access vector",
      acronym: "AV",
      description:
        "This metric reflects how the vulnerability is exploited. The more remote an attacker can be to attack a host, the greater the vulnerability score.",
      values: {
        n: {
          title: "Network",
          acronym: "N",
          scalar: 1.0,
          color: theme.palette.critical.main,
        },
        a: {
          title: "Adjacent network",
          acronym: "A",
          scalar: 0.65,
          color: theme.palette.medium.main,
        },
        l: {
          title: "Local",
          acronym: "L",
          scalar: 0.25,
          color: theme.palette.low.main,
        },
      },
    },
    ac: {
      title: "Access complexity",
      acronym: "AC",
      description:
        "This metric measures the complexity of the attack required to exploit the vulnerability once an attacker has gained access to the target system.",
      values: {
        l: {
          title: "Low",
          acronym: "L",
          scalar: 1.0,
          color: theme.palette.critical.main,
        },
        m: {
          title: "Medium",
          acronym: "M",
          scalar: 0.65,
          color: theme.palette.medium.main,
        },
        h: {
          title: "High",
          acronym: "H",
          scalar: 0.25,
          color: theme.palette.low.main,
        },
      },
    },
    au: {
      title: "Authentication",
      acronym: "Au",
      description:
        "This metric measures the number of times an attacker must authenticate to a target in order to exploit a vulnerability.",
      values: {
        n: {
          title: "None",
          acronym: "N",
          scalar: 1.0,
          color: theme.palette.critical.main,
        },
        s: {
          title: "Single",
          acronym: "S",
          scalar: 0.65,
          color: theme.palette.medium.main,
        },
        m: {
          title: "Multiple",
          acronym: "M",
          scalar: 0.25,
          color: theme.palette.low.main,
        },
      },
    },
    c: {
      title: "Confidentiality",
      acronym: "C",
      description:
        "This metric measures the impact on confidentiality of a successfully exploited vulnerability.",
      values: {
        c: {
          title: "Complete",
          acronym: "C",
          scalar: 1.0,
          color: theme.palette.critical.main,
        },
        p: {
          title: "Partial",
          acronym: "P",
          scalar: 0.65,
          color: theme.palette.medium.main,
        },
        n: {
          title: "None",
          acronym: "N",
          scalar: 0.25,
          color: theme.palette.low.main,
        },
      },
    },
    i: {
      title: "Impact",
      acronym: "I",
      description:
        "This metric measures the impact to integrity of a successfully exploited vulnerability.",
      values: {
        c: {
          title: "Complete",
          acronym: "C",
          scalar: 1.0,
          color: theme.palette.critical.main,
        },
        p: {
          title: "Partial",
          acronym: "P",
          scalar: 0.65,
          color: theme.palette.medium.main,
        },
        n: {
          title: "None",
          acronym: "N",
          scalar: 0.25,
          color: theme.palette.low.main,
        },
      },
    },
    a: {
      title: "Availability",
      acronym: "A",
      description:
        "This metric measures the impact to availability of a successfully exploited vulnerability.",
      values: {
        c: {
          title: "Complete",
          acronym: "C",
          scalar: 1.0,
          color: theme.palette.critical.main,
        },
        p: {
          title: "Partial",
          acronym: "P",
          scalar: 0.65,
          color: theme.palette.medium.main,
        },
        n: {
          title: "None",
          acronym: "N",
          scalar: 0.25,
          color: theme.palette.low.main,
        },
      },
    },
  };
};

export const cvss3Fields = (theme: Theme): CvssFields => {
  return {
    av: {
      title: "Attack vector",
      acronym: "AV",
      description:
        "This metric reflects the context by which vulnerability exploitation is possible. This metric value will be larger the more remote an attacker can be in order to exploit the vulnerable component.",
      values: {
        n: {
          title: "Network",
          acronym: "N",
          scalar: 1.0,
          color: theme.palette.critical.main,
        },
        a: {
          title: "Adjacent",
          acronym: "A",
          scalar: 0.75,
          color: theme.palette.high.main,
        },
        l: {
          title: "Local",
          acronym: "L",
          scalar: 0.5,
          color: theme.palette.medium.main,
        },
        p: {
          title: "Physical",
          acronym: "P",
          scalar: 0.25,
          color: theme.palette.low.main,
        },
      },
    },
    ac: {
      title: "Attack complexity",
      acronym: "AC",
      description:
        "This metric describes the conditions beyond the attacker's control that must exist in order to exploit the vulnerability.",
      values: {
        l: {
          title: "Low",
          acronym: "L",
          scalar: 1.0,
          color: theme.palette.critical.main,
        },
        h: {
          title: "High",
          acronym: "H",
          scalar: 0.25,
          color: theme.palette.low.main,
        },
      },
    },
    pr: {
      title: "Privileges required",
      acronym: "PR",
      description:
        "This metric describes the level of privileges an attacker must possess before successfully exploiting the vulnerability.",
      values: {
        n: {
          title: "None",
          acronym: "N",
          scalar: 1.0,
          color: theme.palette.critical.main,
        },
        l: {
          title: "Low",
          acronym: "L",
          scalar: 0.63,
          color: theme.palette.high.main,
        },
        h: {
          title: "High",
          acronym: "H",
          scalar: 0.25,
          color: theme.palette.low.main,
        },
      },
    },
    ui: {
      title: "User interaction",
      acronym: "UI",
      description:
        "This metric captures the requirement for a user, other than the attacker, to participate in the successful compromise of the vulnerable component.",
      values: {
        n: {
          title: "None",
          acronym: "N",
          scalar: 1.0,
          color: theme.palette.critical.main,
        },
        r: {
          title: "Required",
          acronym: "R",
          scalar: 0.25,
          color: theme.palette.low.main,
        },
      },
    },
    s: {
      title: "Scope",
      acronym: "S",
      description:
        "Scope refers to the collection of privileges defined by a computing authority when granting access to computing resources.",
      values: {
        c: {
          title: "Changed",
          acronym: "C",
          scalar: 1.0,
          color: theme.palette.critical.main,
        },
        u: {
          title: "Unchanged",
          acronym: "U",
          scalar: 0.25,
          color: theme.palette.low.main,
        },
      },
    },
    c: {
      title: "Confidentiality",
      acronym: "C",
      description:
        "This metric measures the impact to the confidentiality of the information resources managed by a software component due to a successfully exploited vulnerability.",
      values: {
        h: {
          title: "High",
          acronym: "H",
          scalar: 1.0,
          color: theme.palette.critical.main,
        },
        l: {
          title: "Low",
          acronym: "L",
          scalar: 0.63,
          color: theme.palette.high.main,
        },
        n: {
          title: "None",
          acronym: "N",
          scalar: 0.25,
          color: theme.palette.low.main,
        },
      },
    },
    i: {
      title: "Integrity",
      acronym: "I",
      description:
        "This metric measures the impact to integrity of a successfully exploited vulnerability. Integrity refers to the trustworthiness and veracity of information.",
      values: {
        h: {
          title: "High",
          acronym: "H",
          scalar: 1.0,
          color: theme.palette.critical.main,
        },
        l: {
          title: "Low",
          acronym: "L",
          scalar: 0.63,
          color: theme.palette.high.main,
        },
        n: {
          title: "None",
          acronym: "N",
          scalar: 0.25,
          color: theme.palette.low.main,
        },
      },
    },
    a: {
      title: "Availability",
      acronym: "A",
      description:
        "This metric measures the impact to the availability of the impacted component resulting from a successfully exploited vulnerability.",
      values: {
        h: {
          title: "High",
          acronym: "H",
          scalar: 1.0,
          color: theme.palette.critical.main,
        },
        l: {
          title: "Low",
          acronym: "L",
          scalar: 0.63,
          color: theme.palette.high.main,
        },
        n: {
          title: "None",
          acronym: "N",
          scalar: 0.25,
          color: theme.palette.low.main,
        },
      },
    },
  };
};

export const cvss4Fields = (theme: Theme): CvssFields => {
  return {
    av: {
      title: "Attack vector",
      acronym: "AV",
      description:
        "This metric reflects the context by which vulnerability exploitation is possible. This metric value will be larger the more remote an attacker can be in order to exploit the vulnerable component.",
      values: {
        n: {
          title: "Network",
          acronym: "N",
          scalar: 1.0,
          color: theme.palette.critical.main,
        },
        a: {
          title: "Adjacent",
          acronym: "A",
          scalar: 0.75,
          color: theme.palette.high.main,
        },
        l: {
          title: "Local",
          acronym: "L",
          scalar: 0.5,
          color: theme.palette.medium.main,
        },
        p: {
          title: "Physical",
          acronym: "P",
          scalar: 0.25,
          color: theme.palette.low.main,
        },
      },
    },
    ac: {
      title: "Attack complexity",
      acronym: "AC",
      description:
        "This metric describes the conditions beyond the attacker's control that must exist in order to exploit the vulnerability.",
      values: {
        l: {
          title: "Low",
          acronym: "L",
          scalar: 1.0,
          color: theme.palette.critical.main,
        },
        h: {
          title: "High",
          acronym: "H",
          scalar: 0.25,
          color: theme.palette.low.main,
        },
      },
    },
    at: {
      title: "Attack requirements",
      acronym: "AT",
      description:
        "This metric describes the conditions beyond the attacker's control that must exist in order to exploit the vulnerability.",
      values: {
        n: {
          title: "None",
          acronym: "N",
          scalar: 1.0,
          color: theme.palette.critical.main,
        },
        p: {
          title: "Present",
          acronym: "P",
          scalar: 0.63,
          color: theme.palette.high.main,
        },
      },
    },
    pr: {
      title: "Privileges required",
      acronym: "PR",
      description:
        "This metric describes the level of privileges an attacker must possess before successfully exploiting the vulnerability.",
      values: {
        n: {
          title: "None",
          acronym: "N",
          scalar: 1.0,
          color: theme.palette.critical.main,
        },
        l: {
          title: "Low",
          acronym: "L",
          scalar: 0.63,
          color: theme.palette.high.main,
        },
        h: {
          title: "High",
          acronym: "H",
          scalar: 0.25,
          color: theme.palette.low.main,
        },
      },
    },
    ui: {
      title: "User interaction",
      acronym: "UI",
      description:
        "This metric captures the requirement for a user, other than the attacker, to participate in the successful compromise of the vulnerable component.",
      values: {
        n: {
          title: "None",
          acronym: "N",
          scalar: 1.0,
          color: theme.palette.critical.main,
        },
        p: {
          title: "Passive",
          acronym: "P",
          scalar: 0.63,
          color: theme.palette.high.main,
        },
        a: {
          title: "Active",
          acronym: "A",
          scalar: 0.25,
          color: theme.palette.low.main,
        },
      },
    },
    vc: {
      title: "Vulnerable System Confidentiality Impact",
      acronym: "VC",
      description:
        "This metric measures the impact to the confidentiality of the information resources managed by a software component due to a successfully exploited vulnerability.",
      values: {
        h: {
          title: "High",
          acronym: "H",
          scalar: 1.0,
          color: theme.palette.critical.main,
        },
        l: {
          title: "Low",
          acronym: "L",
          scalar: 0.63,
          color: theme.palette.high.main,
        },
        n: {
          title: "None",
          acronym: "N",
          scalar: 0.25,
          color: theme.palette.low.main,
        },
      },
    },
    vi: {
      title: "Vulnerable System Integrity Impact",
      acronym: "VI",
      description:
        "This metric measures the impact to the confidentiality of the information resources managed by a software component due to a successfully exploited vulnerability.",
      values: {
        h: {
          title: "High",
          acronym: "H",
          scalar: 1.0,
          color: theme.palette.critical.main,
        },
        l: {
          title: "Low",
          acronym: "L",
          scalar: 0.63,
          color: theme.palette.high.main,
        },
        n: {
          title: "None",
          acronym: "N",
          scalar: 0.25,
          color: theme.palette.low.main,
        },
      },
    },
    va: {
      title: "Vulnerable System Availability Impact",
      acronym: "VA",
      description:
        "This metric measures the impact to the confidentiality of the information resources managed by a software component due to a successfully exploited vulnerability.",
      values: {
        h: {
          title: "High",
          acronym: "H",
          scalar: 1.0,
          color: theme.palette.critical.main,
        },
        l: {
          title: "Low",
          acronym: "L",
          scalar: 0.63,
          color: theme.palette.high.main,
        },
        n: {
          title: "None",
          acronym: "N",
          scalar: 0.25,
          color: theme.palette.low.main,
        },
      },
    },
    sc: {
      title: "Subsequent System Confidentiality Impact",
      acronym: "SC",
      description:
        "This metric measures the impact to the confidentiality of the information resources managed by a software component due to a successfully exploited vulnerability.",
      values: {
        h: {
          title: "High",
          acronym: "H",
          scalar: 1.0,
          color: theme.palette.critical.main,
        },
        l: {
          title: "Low",
          acronym: "L",
          scalar: 0.63,
          color: theme.palette.high.main,
        },
        n: {
          title: "None",
          acronym: "N",
          scalar: 0.25,
          color: theme.palette.low.main,
        },
      },
    },
    si: {
      title: "Subsequent System Integrity Impact",
      acronym: "SI",
      description:
        "This metric measures the impact to the confidentiality of the information resources managed by a software component due to a successfully exploited vulnerability.",
      values: {
        h: {
          title: "High",
          acronym: "H",
          scalar: 1.0,
          color: theme.palette.critical.main,
        },
        l: {
          title: "Low",
          acronym: "L",
          scalar: 0.63,
          color: theme.palette.high.main,
        },
        n: {
          title: "None",
          acronym: "N",
          scalar: 0.25,
          color: theme.palette.low.main,
        },
      },
    },
    sa: {
      title: "Subsequent System Availability Impact",
      acronym: "SA",
      description:
        "This metric measures the impact to the confidentiality of the information resources managed by a software component due to a successfully exploited vulnerability.",
      values: {
        h: {
          title: "High",
          acronym: "H",
          scalar: 1.0,
          color: theme.palette.critical.main,
        },
        l: {
          title: "Low",
          acronym: "L",
          scalar: 0.63,
          color: theme.palette.high.main,
        },
        n: {
          title: "None",
          acronym: "N",
          scalar: 0.25,
          color: theme.palette.low.main,
        },
      },
    },
  };
};

export const cvssFields = (version: CvssVersion, theme: Theme): CvssFields => {
  switch (version) {
    case "2.0":
      return cvss2Fields(theme);
    case "3.0":
    case "3.1":
      return cvss3Fields(theme);
    case "4.0":
      return cvss4Fields(theme);
  }
};

export const cvssScore = (vector: string): number => {
  return parseFloat(vector.split("/")[0]);
};

export const cvssVersion = (vector: string): CvssVersion => {
  const [_, version] = CVSS_VERSION_RX.exec(vector) || [NONE_VECTOR, "3.0"];
  return isCvssVersion(version) ? version : "2.0";
};

export const cvssDisplayVersion = (vector: string): string => {
  const [displayVersion] = cvssVersion(vector).split(".0");
  return displayVersion;
};

export const parseCvss = (vector: string, theme: Theme) => {
  const parts = vector.split("/");
  const score = cvssScore(vector);
  const rating = getImpactLabelUpdated(CvssScoreType(score * 10));
  const version = cvssVersion(vector);
  const fields = cvssFields(version, theme);
  const rawMetrics = version.startsWith("2") ? parts.slice(1) : parts.slice(2);
  const metrics = rawMetrics.flatMap(
    (m) => cvssMetric(fields)(m.toLowerCase()) ?? [],
  );
  return {
    version,
    score,
    rating,
    metrics,
  };
};
